import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { CurrentUserDto } from '../domain/users/current-user-dto';

/**
 * Singleton signal-backed cache of the signed-in admin's identity + customer name.
 * The dashboard shell loads it once on init; any component reads <code>me()</code>
 * synchronously afterward. JWT carries no customer claim, so this exists to fill
 * that gap without every component fetching /users/me independently.
 */
@Injectable({ providedIn: 'root' })
export class CurrentUserStore {
  private api = inject(ApiService);

  readonly me = signal<CurrentUserDto | null>(null);
  readonly permissions = computed(() => this.me()?.permissions ?? []);
  /**
   * Statewide = top-level operator: Admin with no tenant binding. These users
   * can manage all customers and create/edit global roles. Kept in sync with
   * BaseController.IsStatewide on the server.
   */
  readonly isStatewide = computed(() => {
    const u = this.me();
    return u !== null && u.role === 'Admin' && u.customerId == null;
  });

  /**
   * Screener-only = applicant access scoped to JobPostingScreener assignments.
   * Holds <code>ViewAssignedApplicants</code> and lacks the broader <code>ViewApplicants</code>.
   * Branches the dashboard to the screener overview and trims the menu. Kept in
   * sync with BaseController.IsScreenerOnly on the server.
   */
  readonly isScreenerOnly = computed(() => {
    const perms = this.permissions();
    return perms.includes('ViewAssignedApplicants') && !perms.includes('ViewApplicants');
  });

  /**
   * Billing-only = the user holds <code>ViewBilling</code> but no broader "feature"
   * permission. Their landing experience is the billing-only menu + dashboard.
   * Any user holding ViewPostings, ViewApplicants (or assigned), ViewApplicantBank,
   * or ManageCustomers gets the full tenant menu instead. Statewide Admins
   * resolve via <code>isStatewide</code> first so this never fires for them.
   */
  readonly isBillingOnly = computed(() => {
    const p = this.permissions();
    return p.includes('ViewBilling')
      && !p.includes('ViewPostings')
      && !p.includes('ViewApplicants')
      && !p.includes('ViewAssignedApplicants')
      && !p.includes('ViewApplicantBank')
      && !p.includes('ManageCustomers');
  });

  /**
   * True when the signed-in user is their customer's designated billing contact —
   * identity-based (not a permission): the only user who may sign the service
   * agreement, and guaranteed access to view it. Server-computed on /users/me.
   */
  readonly isBillingContact = computed(() => this.me()?.isBillingContact ?? false);

  /** True when the signed-in user holds <code>permissionKey</code>. Reactive. */
  hasPermission(permissionKey: string): boolean {
    return this.permissions().includes(permissionKey);
  }

  private inflight: Promise<void> | null = null;

  /** Idempotent: safe to call from multiple places during startup. */
  async load(): Promise<void> {
    if (this.me() !== null) return;
    if (this.inflight) return this.inflight;
    this.inflight = (async () => {
      try {
        const me = await firstValueFrom(this.api.get<CurrentUserDto>('users/me'));
        this.me.set(me);
      } catch {
        // Non-fatal — the UI degrades to showing nothing in the footer.
        this.me.set(null);
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  }

  /** Clears the cache; call on logout so a subsequent sign-in fetches fresh data. */
  clear(): void {
    this.me.set(null);
  }
}
